/**
 * The V7 escalation ladder as the console draws it (design care-flow.js, AithoraCF.compute / row /
 * coming): a task belongs to one person, and if it is not done it moves on by itself:
 *
 *   owner -> charge nurse -> on-call nurse -> everyone on shift (Director of Nursing told)
 *
 * Decision 2026-10-14: the server runs the ladder (src/aithora/v3/worklist/ladder-rule.ts) and
 * sends each task's and request's `ladder`: its urgency, the step it has reached, when it moves
 * next and who has taken it. This file only words that state; it keeps no clock and no rule of
 * its own, so a screen can never say "moves on in 2:00" while the server moves it at 15.
 * English only: the Spanish comes from the console's dictionary (es-extra.ts), as everywhere.
 */
import { taskUrgencyFromText, type TaskLadderLevel, type TaskLadderView } from '@aithora/shared';

export interface CfLevel {
  label: string;
  ink: string;
  bg: string;
}

/**
 * The server's ladder for a task or request. A row from a server that sends none (an older
 * build) shows its level from the shared words rule (packages/shared task-urgency.ts) and stays
 * with its owner: the console never moves anything on its own clock.
 */
export function cfView(ladder: TaskLadderView | null | undefined, words: string, startIso: string, opts: { urgent?: boolean; closed?: boolean } = {}): TaskLadderView {
  if (ladder) return ladder;
  const level: TaskLadderLevel = opts.urgent ? 'urgent' : taskUrgencyFromText(words);
  return {
    level,
    steps: level === 'routine' ? ['owner', 'charge_nurse', 'everyone'] : ['owner', 'charge_nurse', 'on_call_nurse', 'everyone'],
    minutes: [],
    startedAt: startIso,
    step: 0,
    stepAt: [startIso],
    nextMoveAt: null,
    claimedBy: null,
    claimedAt: null,
    closed: !!opts.closed,
    // No server ladder: nothing moves on by itself, so no countdown.
    ladderOn: false,
  };
}

/** Whether the server runs the ladder: its own word; a view from an older server that sends none, by its rule. */
export function cfLadderOn(v: TaskLadderView): boolean {
  return v.ladderOn ?? v.minutes.length > 0;
}

/**
 * The step the server's rule has reached at `now`, from the server's own minutes: a row loaded
 * just before a move never sits at 0:00 waiting for the next refresh. Off, nothing moves.
 */
export function cfReached(v: TaskLadderView, now: number): { step: number; stepEnd: number | null; stepAt: number[] } {
  const last = v.steps.length - 1;
  const start = Date.parse(v.startedAt);
  let step = Math.max(0, Math.min(last, v.step));
  const stepAt = v.stepAt.slice(0, step + 1).map((t) => Date.parse(t));
  if (!cfLadderOn(v)) return { step, stepEnd: null, stepAt };
  let stepEnd = v.nextMoveAt ? Date.parse(v.nextMoveAt) : null;
  const at = (k: number) => start + v.minutes.slice(0, k).reduce((a, b) => a + b, 0) * 60_000;
  while (stepEnd !== null && Number.isFinite(stepEnd) && now >= stepEnd && step < last && v.minutes.length >= step + 1) {
    step += 1;
    stepAt.push(stepEnd);
    stepEnd = step < last && v.minutes.length >= step + 1 ? at(step + 1) : null;
  }
  return { step, stepEnd: step >= last ? null : stepEnd, stepAt };
}

/** The design's level chips. */
export const CF_LEVELS: Record<TaskLadderLevel, CfLevel> = {
  urgent: { label: 'Urgent', ink: '#B3261E', bg: 'rgba(179,38,30,.11)' },
  soon: { label: 'Soon', ink: '#7A4A0E', bg: 'rgba(194,112,47,.16)' },
  routine: { label: 'Routine', ink: '#2F5C97', bg: 'rgba(47,92,151,.11)' },
};

export interface CfTask {
  id: string;
  /** The server's ladder for this task or request. */
  view: TaskLadderView;
  /** 'You', a person's name, a role in words; null when nobody owns it. */
  owner: string | null;
  /** The charge nurse on shift by name, or null (the chip says "Charge nurse"). */
  charge: string | null;
  /** Who has it now, in words ('You' for the viewer), when the server says someone took it. */
  claimedBy: string | null;
  /** Who had it before the ladder first moved it, in words ('You' for the viewer); the server's firstOwnerName. */
  firstOwner?: string | null;
  done?: boolean;
}

export interface CfChip {
  t: string;
  s: string;
}

export interface CfResult {
  step: number;
  last: number;
  names: string[];
  text: string;
  ink: string;
  chips: CfChip[];
  level: CfLevel;
  audit: string;
  claimed: boolean;
  /** When the server moves it next; null when it will not move again. */
  stepEnd: number | null;
  /** The server runs the ladder; off, no row shows a countdown. */
  on: boolean;
}

/** "4:07", or "1 h 7 m" past an hour (the design's mmss). */
export function mmss(ms: number): string {
  const t = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${m}:${String(s).padStart(2, '0')}`;
}

const clock = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const GENERIC = /^(Charge nurse|On-call nurse)$/;

/** The design's AithoraCF.compute, worded from the server's state. */
export function cfCompute(t: CfTask, now: number): CfResult {
  const v = t.view;
  const L = CF_LEVELS[v.level] ?? CF_LEVELS.routine;
  const owner = t.owner || 'Unassigned';
  const first = v.step > 0 ? t.firstOwner ?? owner : owner;
  const names = v.steps.map((s) => (s === 'owner' ? first : s === 'charge_nurse' ? t.charge || 'Charge nurse' : s === 'on_call_nurse' ? 'On-call nurse' : 'Everyone on shift'));
  const last = names.length - 1;
  const on = cfLadderOn(v);
  const claimed = !!(t.claimedBy || v.claimedBy);
  const stopped = claimed || !!t.done || v.closed;
  const r = stopped ? { step: Math.max(0, Math.min(last, v.step)), stepEnd: v.nextMoveAt ? Date.parse(v.nextMoveAt) : null, stepAt: v.stepAt.map((x) => Date.parse(x)) } : cfReached(v, now);
  const step = Math.max(0, Math.min(last, r.step));
  const who = t.claimedBy ?? v.claimedBy?.name ?? 'Someone';
  const start = Date.parse(v.startedAt);
  const stepEnd = r.stepEnd;
  const reachedAt = r.stepAt[step] ?? start;
  let text: string;
  let ink: string;
  if (t.done || v.closed) {
    text = 'Done';
    ink = '#1F6B52';
  } else if (claimed) {
    text = `${who === 'You' ? 'You’re on it' : `${who} is on it`} · clock stopped ${clock(v.claimedAt ? Date.parse(v.claimedAt) : start)}`;
    ink = '#1F6B52';
  } else if (!on) {
    // The server does not run the ladder: nothing moves on by itself, so no countdown.
    const holder = step === 0 ? (owner === 'You' ? 'Yours' : owner) : names[step];
    text = `Ladder off · ${holder}`;
    ink = '#3C454F';
  } else if (now < start) {
    text = `${names[0]} · starts in ${mmss(start - now)}`;
    ink = '#3C454F';
  } else if (step === last) {
    text = `Everyone on shift alerted ${mmss(now - reachedAt)} ago · Director of Nursing told`;
    ink = '#B3261E';
  } else if (step === 0) {
    text = `${owner === 'You' ? 'Yours' : owner} · moves to ${names[1]} in ${mmss((stepEnd ?? now) - now)}`;
    ink = (stepEnd ?? now) - now < 60_000 ? '#9A5520' : '#3C454F';
  } else {
    const whose = (n: string) => (GENERIC.test(n) ? `the ${n.toLowerCase()}` : n);
    const prevName = names[step - 1];
    const prev =
      step === 1 && first === 'You'
        ? 'You didn’t get to it'
        : `${step === 1 ? first : GENERIC.test(prevName) ? `The ${prevName.toLowerCase()}` : prevName} didn’t get to it`;
    text = `${prev} · now ${whose(names[step])}’s · moves on in ${mmss((stepEnd ?? now) - now)}`;
    ink = '#9A5520';
  }
  const chip = (i: number): string => {
    const base = 'display:inline-flex;align-items:center;gap:4px;min-height:24px;padding:2px 9px;border-radius:12px;font-size:14px;font-weight:700;white-space:nowrap;';
    if (t.done || v.closed || claimed) return base + (i === 0 ? 'color:#12503C;background:rgba(31,107,82,.13);' : 'color:#54606C;background:transparent;border:1px dashed rgba(34,39,46,.2);');
    if (i < step) return base + 'color:#54606C;background:rgba(34,39,46,.07);text-decoration:line-through;';
    if (i === step) return base + 'color:#FFFFFF;background:' + (step === 0 ? '#264C7E' : step === last ? '#B3261E' : '#9A5520') + ';';
    return base + 'color:#54606C;background:transparent;border:1px solid rgba(34,39,46,.3);';
  };
  const audit = r.stepAt
    .slice(0, step + 1)
    .map((ts, i) => `${names[i]} ${clock(ts)}`)
    .join(' → ');
  return { step, last, names, text, ink, chips: names.map((n, i) => ({ t: n, s: chip(i) })), level: L, audit, claimed, stepEnd, on };
}

/** The design's row styles for the ladder line under a task (AithoraCF.row). */
export const CF_TEXT_STYLE = (ink: string) => `font-size:15px;font-weight:700;line-height:1.35;color:${ink};font-variant-numeric:tabular-nums;`;
export const CF_LEVEL_STYLE = (L: CfLevel) =>
  `display:inline-flex;align-items:center;min-height:22px;padding:1px 7px;border-radius:5px;font-size:14px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;white-space:nowrap;color:${L.ink};background:${L.bg};`;

/** One task the "Moving up the ladder" box may list. */
export interface CfCandidate {
  task: CfTask;
  /** "Arthur", or "Resident I. (12B)" for a masked name. */
  who: string;
  room?: string | null;
  /** The task or request kind, e.g. "TOILETING" (written in normal case). */
  what: string;
  ref: unknown;
}

export interface CfComingRow {
  id: string;
  task: CfTask;
  ref: unknown;
  label: string;
  text: string;
  ink: string;
  dot: string;
  /** People on shift it can be given to: everyone on shift minus the current holder and the owner. */
  picks: { userId: string; name: string }[];
}

export interface CfComing {
  show: boolean;
  n: number;
  sum: string;
  rows: CfComingRow[];
  moreShow: boolean;
  moreLabel: string;
  /** The server runs the ladder; off, the box says so and lists the late tasks without a countdown. */
  on: boolean;
}

const tc = (w: string) => (w && w === w.toUpperCase() && /[A-Z]/.test(w) ? w.charAt(0) + w.slice(1).toLowerCase() : w);

/**
 * The design's AithoraCF.coming: what the server has already moved to the next person, or will
 * move inside 90 seconds, newest step first. A read-only role sees the count and the summary only.
 */
export function cfComing(list: CfCandidate[], o: { now: number; canAssign: boolean; open: boolean; staff: { userId: string; name: string }[]; ladderOn?: boolean }): CfComing {
  const seen = new Set<string>();
  const items: { c: CfCandidate; r: CfResult; left: number }[] = [];
  // Off when the route says so, or when every row's own view says so.
  const on = o.ladderOn ?? list.some((c) => cfLadderOn(c.task.view));
  for (const c of list) {
    const t = c.task;
    if (seen.has(t.id) || t.done || t.view.closed || t.claimedBy || t.view.claimedBy) continue;
    seen.add(t.id);
    if (!(Date.parse(t.view.startedAt) <= o.now)) continue;
    const r = cfCompute(t, o.now);
    const left = r.stepEnd ? r.stepEnd - o.now : 0;
    // Off: every late task, so someone can still take it; on: moved, or moving inside 90 seconds.
    if (on && !(r.step >= 1 || (r.stepEnd !== null && left < 90_000))) continue;
    items.push({ c, r, left: on ? left : Date.parse(t.view.startedAt) - o.now });
  }
  items.sort((a, b) => b.r.step - a.r.step || a.left - b.left);
  const top = items.filter((it) => it.r.step === it.r.last).length;
  const mid = items.filter((it) => it.r.step === 1 && it.r.step < it.r.last).length;
  const mid2 = items.filter((it) => it.r.step >= 2 && it.r.step < it.r.last).length;
  const soon = items.length - top - mid - mid2;
  const sum = !on
    ? 'The ladder is off: nothing moves up by itself. Late tasks stay where they are until someone takes them.'
    : [
    top ? `${top} reached everyone on shift` : '',
    mid ? `${mid} with the charge nurse` : '',
    mid2 ? `${mid2} with the on-call nurse` : '',
    soon ? `${soon} about to move` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const cap = o.canAssign ? (o.open ? items.length : 1) : o.open ? items.length : 0;
  const rows: CfComingRow[] = items.slice(0, cap).map(({ c, r }) => {
    let who = c.who;
    if (/^[A-Z]\.$/.test(who)) who = `Resident ${who}${c.room && !/^Room /.test(c.room) ? ` (${c.room})` : ''}`;
    const dotInk = r.step === r.last ? '#B3261E' : r.step >= 1 ? '#9A5520' : '#264C7E';
    const holder = r.names[r.step];
    return {
      id: c.task.id,
      task: c.task,
      ref: c.ref,
      label: [who, tc(c.what)].filter(Boolean).join(' · '),
      text: r.text,
      ink: `font-size:15px;font-weight:600;line-height:1.35;font-variant-numeric:tabular-nums;color:${r.ink};`,
      dot: `flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:${dotInk};`,
      picks: o.staff.filter((p) => p.name !== holder && p.name !== c.task.owner),
    };
  });
  const hidden = items.length - (o.canAssign ? Math.min(1, items.length) : 0);
  return {
    show: items.length > 0,
    n: items.length,
    sum,
    rows,
    moreShow: hidden > 0,
    moreLabel: o.open ? 'Show less' : o.canAssign ? `Show ${hidden} more` : 'Show list',
    on,
  };
}
